import { useEffect, useState } from "react";
import { format, parse, isValid } from "date-fns";
import { id as idLocale } from "date-fns/locale";
import { CalendarDays } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";

const DISPLAY = "dd/MM/yyyy";
const ISO = "yyyy-MM-dd";
const toDate = (s) => { const d = s ? parse(String(s).slice(0, 10), ISO, new Date()) : null; return d && isValid(d) ? d : null; };
const show = (s) => { const d = toDate(s); return d ? format(d, DISPLAY) : ""; };

export const fmtDate = (s) => (/^\d{4}-\d{2}-\d{2}/.test(String(s || "")) ? show(s) : s || "");

function parseTyped(txt) {
  const s = txt.trim();
  const m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(s) || null;
  const d = m ? parse(`${m[1]}/${m[2]}/${m[3]}`, "d/M/yyyy", new Date()) : /^\d{4}-\d{1,2}-\d{1,2}$/.test(s) ? parse(s, "yyyy-M-d", new Date()) : null;
  return d && isValid(d) && d.getFullYear() > 1900 ? format(d, ISO) : null;
}

const CAL_CLASSES = {
  caption_dropdowns: "flex justify-center gap-1",
  dropdown_month: "relative inline-flex items-center",
  dropdown_year: "relative inline-flex items-center",
  dropdown: "absolute inset-0 z-10 w-full cursor-pointer opacity-0",
  dropdown_icon: "h-3 w-3",
  vhidden: "sr-only",
  caption_label: "inline-flex items-center gap-1 px-1 text-sm font-medium capitalize",
};

export function DateInput({ value, onChange, className, min, max, title, disabled, placeholder = "dd/mm/yyyy", "data-testid": testid }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(show(value));
  useEffect(() => { setText(show(value)); }, [value]);
  const emit = (iso) => { if (onChange && iso !== (value || "")) onChange({ target: { value: iso } }); };
  const inRange = (iso) => !iso || ((!min || iso >= String(min).slice(0, 10)) && (!max || iso <= String(max).slice(0, 10)));
  const typed = (v) => { setText(v); const iso = parseTyped(v); if (iso && inRange(iso)) emit(iso); };
  const commit = () => {
    if (!text.trim()) { emit(""); return; }
    const iso = parseTyped(text);
    if (iso && inRange(iso)) emit(iso); else setText(show(value));
  };
  const sel = toDate(value);
  const limits = [min && toDate(min) && { before: toDate(min) }, max && toDate(max) && { after: toDate(max) }].filter(Boolean);
  const pick = (iso) => { emit(iso); setOpen(false); };
  return (
    <div className={cn("relative flex items-center", className)} title={title}>
      <input value={text} disabled={disabled} placeholder={placeholder} inputMode="numeric" data-testid={testid}
        onChange={(e) => typed(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } }}
        className="num flex h-9 w-full rounded-md border border-input bg-transparent pl-3 pr-8 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50" />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" disabled={disabled} aria-label="Pilih tanggal" data-testid={testid ? `${testid}-cal` : undefined}
            className="absolute right-1 inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-accent hover:text-foreground">
            <CalendarDays className="h-4 w-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" locale={idLocale} weekStartsOn={1} selected={sel || undefined} defaultMonth={sel || undefined}
            captionLayout="dropdown-buttons" fromYear={1990} toYear={2045} disabled={limits} classNames={CAL_CLASSES}
            onSelect={(d) => { if (d) pick(format(d, ISO)); }} />
          <div className="flex items-center justify-between border-t border-border px-3 py-2 text-xs">
            <button type="button" className="font-medium text-primary hover:underline" onClick={() => pick(format(new Date(), ISO))} data-testid={testid ? `${testid}-today` : undefined}>Hari ini</button>
            <button type="button" className="text-muted-foreground hover:text-foreground hover:underline" onClick={() => pick("")} data-testid={testid ? `${testid}-clear` : undefined}>Hapus</button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
